/**
 * Launch countdown — shown to everyone but admins until LAUNCH_AT
 * (App.js), then the app opens by itself without a reload.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import MasLogo from '../assets/Mas_logo.png';

const pad = (n) => String(n).padStart(2, '0');

const split = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return {
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60,
  };
};

// One digit block; re-keys on change so the tick animation replays
function Unit({ value, label }) {
  const text = pad(value);
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Box
        sx={{
          position: 'relative',
          width: { xs: 68, sm: 104 },
          height: { xs: 80, sm: 120 },
          borderRadius: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.03) 100%)',
          border: '1px solid rgba(255,255,255,0.14)',
          boxShadow: '0 10px 40px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.12)',
          backdropFilter: 'blur(10px)',
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            top: '50%',
            height: '1px',
            background: 'rgba(0,0,0,0.35)',
          },
        }}
      >
        <Typography
          key={text}
          sx={{
            fontFamily: '"Roboto Mono", "SF Mono", Menlo, monospace',
            fontWeight: 700,
            fontSize: { xs: 38, sm: 60 },
            color: '#fff',
            letterSpacing: 2,
            textShadow: '0 0 18px rgba(229,57,53,0.55)',
            animation: 'lc-tick 0.45s cubic-bezier(.2,.8,.2,1)',
          }}
        >
          {text}
        </Typography>
      </Box>
      <Typography sx={{ mt: 1.2, fontSize: { xs: 10, sm: 12 }, letterSpacing: 3, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase' }}>
        {label}
      </Typography>
    </Box>
  );
}

// Slowly rising sparks behind everything
function Sparks() {
  const sparks = useRef(
    Array.from({ length: 28 }, (_, i) => ({
      left: Math.random() * 100,
      size: 2 + Math.random() * 3,
      duration: 9 + Math.random() * 12,
      delay: -Math.random() * 20,
      red: i % 3 === 0,
    }))
  ).current;
  return sparks.map((p, i) => (
    <Box
      key={i}
      sx={{
        position: 'absolute',
        bottom: -10,
        left: `${p.left}%`,
        width: p.size,
        height: p.size,
        borderRadius: '50%',
        background: p.red ? '#ff5a52' : 'rgba(255,255,255,0.8)',
        boxShadow: p.red ? '0 0 10px #ff5a52' : '0 0 8px rgba(255,255,255,0.7)',
        opacity: 0,
        animation: `lc-rise ${p.duration}s linear ${p.delay}s infinite`,
        pointerEvents: 'none',
      }}
    />
  ));
}

export default function LaunchCountdown({ launchAt, launchLabel, user, onLogout }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const { days, hours, minutes, seconds } = split(launchAt - now);

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        textAlign: 'center',
        color: '#fff',
        background: 'radial-gradient(ellipse at top, #1c1f2b 0%, #0b0c12 55%, #050507 100%)',
        '@keyframes lc-tick': {
          '0%': { transform: 'translateY(-35%) scale(0.9)', opacity: 0, filter: 'blur(4px)' },
          '100%': { transform: 'none', opacity: 1, filter: 'none' },
        },
        '@keyframes lc-rise': {
          '0%': { transform: 'translateY(0)', opacity: 0 },
          '10%': { opacity: 0.9 },
          '90%': { opacity: 0.6 },
          '100%': { transform: 'translateY(-110vh)', opacity: 0 },
        },
        '@keyframes lc-drift': {
          '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
          '50%': { transform: 'translate(6%, -4%) scale(1.15)' },
        },
        '@keyframes lc-shine': {
          '0%': { backgroundPosition: '-200% 0' },
          '100%': { backgroundPosition: '200% 0' },
        },
        '@keyframes lc-fade': {
          '0%': { opacity: 0, transform: 'translateY(16px)' },
          '100%': { opacity: 1, transform: 'none' },
        },
        '@keyframes lc-pulse': {
          '0%, 100%': { opacity: 0.35 },
          '50%': { opacity: 1 },
        },
      }}
    >
      {/* Glowing colour fields */}
      <Box sx={{ position: 'absolute', width: '60vmax', height: '60vmax', top: '-25vmax', left: '-15vmax', borderRadius: '50%', background: 'radial-gradient(circle, rgba(211,47,47,0.45), transparent 60%)', filter: 'blur(40px)', animation: 'lc-drift 18s ease-in-out infinite' }} />
      <Box sx={{ position: 'absolute', width: '55vmax', height: '55vmax', bottom: '-28vmax', right: '-18vmax', borderRadius: '50%', background: 'radial-gradient(circle, rgba(120,130,170,0.35), transparent 60%)', filter: 'blur(50px)', animation: 'lc-drift 22s ease-in-out -6s infinite' }} />
      {/* Faint grid */}
      <Box sx={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)', backgroundSize: '48px 48px', maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)' }} />
      <Sparks />

      <Box sx={{ position: 'relative', zIndex: 1, animation: 'lc-fade 0.9s ease-out both' }}>
        <Box sx={{ display: 'inline-flex', px: 2.5, py: 1.2, mb: 4, borderRadius: 3, background: 'rgba(255,255,255,0.92)', boxShadow: '0 8px 30px rgba(0,0,0,0.4)' }}>
          <img src={MasLogo} alt="MAS" style={{ height: 40, display: 'block' }} />
        </Box>

        <Typography sx={{ fontSize: { xs: 11, sm: 13 }, letterSpacing: 6, textTransform: 'uppercase', color: '#ff6b63', mb: 1.5 }}>
          Something new is coming
        </Typography>
        <Typography
          component="h1"
          sx={{
            fontWeight: 800,
            fontSize: { xs: 30, sm: 52 },
            lineHeight: 1.1,
            mb: 2,
            background: 'linear-gradient(90deg, #ffffff 0%, #ffb3ae 25%, #ffffff 50%, #ffb3ae 75%, #ffffff 100%)',
            backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            color: 'transparent',
            animation: 'lc-shine 6s linear infinite',
          }}
        >
          The new AI Vacancy Creator
        </Typography>
        <Typography sx={{ maxWidth: 560, mx: 'auto', color: 'rgba(255,255,255,0.7)', fontSize: { xs: 14, sm: 16 }, mb: 5 }}>
          Job posts and designs for every platform, now with a built-in design studio — all in one place.
        </Typography>

        <Box sx={{ display: 'flex', gap: { xs: 1.2, sm: 2.5 }, justifyContent: 'center', alignItems: 'flex-start' }}>
          <Unit value={days} label="Days" />
          <Unit value={hours} label="Hours" />
          <Unit value={minutes} label="Minutes" />
          <Unit value={seconds} label="Seconds" />
        </Box>

        <Typography sx={{ mt: 4, color: 'rgba(255,255,255,0.55)', fontSize: 13, letterSpacing: 1 }}>
          <Box component="span" sx={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', bgcolor: '#ff5a52', mr: 1, verticalAlign: 'middle', animation: 'lc-pulse 1.6s ease-in-out infinite' }} />
          Launching {launchLabel}
        </Typography>

        {user && (
          <Typography sx={{ mt: 3, fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
            Signed in as {user.email} ·{' '}
            <Box component="button" onClick={onLogout} sx={{ background: 'none', border: 0, p: 0, color: 'rgba(255,255,255,0.7)', cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}>
              Sign out
            </Box>
          </Typography>
        )}
      </Box>

      {!user && (
        <Typography
          component={RouterLink}
          to="/login?admin=1"
          sx={{ position: 'absolute', right: 14, bottom: 10, zIndex: 1, fontSize: 10, color: 'rgba(255,255,255,0.18)', textDecoration: 'none', '&:hover': { color: 'rgba(255,255,255,0.6)' } }}
        >
          admin
        </Typography>
      )}
    </Box>
  );
}
